import React from 'react';
import { Link } from 'react-router-dom';

export default function UpgradePrompt({ 
  isOpen, 
  onClose, 
  title = "Upgrade Required", 
  message, 
  upgradeOptions = null,
  showTopupOption = false,
  minimumBalance = 0
}) {
  if (!isOpen) return null;

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-semibold text-white dark:text-white">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors dark:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-300 mb-4 dark:text-gray-600">{message}</p>
          
          {showTopupOption && (
            <div className="bg-blue-900/30 border border-blue-500 rounded-lg p-4 mb-4">
              <h4 className="text-blue-300 font-medium mb-2">Add Funds to Your Account</h4>
              <p className="text-blue-200 text-sm mb-3">
                You need at least {formatPrice(minimumBalance)} in your account balance to continue.
              </p>
              <Link
                to="/billing"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors dark:text-white"
                onClick={onClose}
              >
                Add Funds
              </Link>
            </div>
          )}
        </div>

        {upgradeOptions && upgradeOptions.plans && upgradeOptions.plans.length > 0 && (
          <div>
            <h4 className="text-lg font-medium text-white mb-4 dark:text-white">Choose a Plan</h4>
            <div className="grid gap-4 md:grid-cols-2">
              {upgradeOptions.plans.map((plan) => (
                <div 
                  key={plan.id} 
                  className={`border rounded-lg p-4 transition-colors ${
                    plan.isPopular 
                      ? 'border-teal-500 bg-teal-900/20' 
                      : 'border-gray-600 bg-gray-700/50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-semibold text-white dark:text-white">{plan.displayName}</h5>
                    {plan.isPopular && (
                      <span className="bg-teal-600 text-white text-xs px-2 py-1 rounded-full dark:text-white">
                        Most Popular
                      </span>
                    )}
                  </div>
                  
                  {plan.badge && !plan.isPopular && (
                    <p className="text-sm text-teal-400 mb-2">{plan.badge}</p>
                  )}
                  
                  <div className="mb-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-white dark:text-white">
                        {formatPrice(plan.monthlyFee)}
                      </span>
                      <span className="text-gray-400">/month</span>
                    </div>
                    {plan.firstMonthFee && plan.firstMonthFee < plan.monthlyFee && (
                      <p className="text-sm text-teal-400">
                        First month: {formatPrice(plan.firstMonthFee)}
                      </p>
                    )}
                  </div>
                  
                  <ul className="text-sm text-gray-300 space-y-1 mb-4 dark:text-gray-600">
                    <li>• {plan.minutesIncluded} minutes included</li>
                    <li>• {plan.phoneNumbersIncluded} phone number{plan.phoneNumbersIncluded !== 1 ? 's' : ''}</li>
                    <li>• {plan.retentionDays} days recording storage</li>
                    <li>• Unlimited parrots</li>
                  </ul>
                  
                  <Link
                    to={`/pricing?plan=${plan.id}`}
                    className={`block w-full text-center py-2 px-4 rounded-lg font-medium transition-colors ${
                      plan.isPopular
                        ? 'bg-teal-600 hover:bg-teal-700 text-white'
                        : 'bg-gray-600 hover:bg-gray-500 text-white'
                    }`}
                    onClick={onClose}
                  >
                    Choose {plan.displayName}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t border-gray-700">
          <div className="flex justify-between items-center">
            <Link
              to="/pricing"
              className="text-teal-400 hover:text-teal-300 text-sm"
              onClick={onClose}
            >
              View all pricing options
            </Link>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors dark:text-gray-600 dark:text-white"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
